import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users,
  Mail,
  Shield,
  ClipboardCopy,
  Loader2,
  UserPlus,
  FileBarChart,
  Activity,
  Clock,
  Building2,
  Search,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Ship,
  Download,
  AlertCircle,
} from "lucide-react";

const AdminDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("users");

  // Redirect non-admin users
  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/");
      toast.error("Acceso restringido a administradores");
    }
  }, [user, navigate]);

  if (!user || user.role !== "admin") return null;

  const tabs = [
    { id: "users", label: "Usuarios", icon: Users },
    { id: "invitations", label: "Invitaciones", icon: UserPlus },
    { id: "activity", label: "Actividad", icon: Activity },
    { id: "stats", label: "Estadísticas", icon: FileBarChart },
  ];

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Tab Navigation */}
        <div className="flex gap-1 bg-white rounded-lg p-1 border border-slate-200 w-fit" data-testid="admin-tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`admin-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? "bg-[#0b3e65] text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === "users" && <UsersTab />}
        {activeTab === "invitations" && <InvitationsTab />}
        {activeTab === "activity" && <ActivityTab />}
        {activeTab === "stats" && <StatsTab />}
      </div>
    </Layout>
  );
};

// ============== USERS TAB ==============
const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/users`)
      .then((res) => setUsers(res.data))
      .catch(() => toast.error("Error cargando usuarios"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[#0b3e65]">Usuarios registrados ({users.length})</h2>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="bg-[#0b3e65]/5 border-b border-slate-200">
                <th className="text-left px-4 py-3 font-medium text-[#0b3e65]">Nombre</th>
                <th className="text-left px-4 py-3 font-medium text-[#0b3e65]">Email</th>
                <th className="text-left px-4 py-3 font-medium text-[#0b3e65]">Empresa</th>
                <th className="text-left px-4 py-3 font-medium text-[#0b3e65]">Rol</th>
                <th className="text-left px-4 py-3 font-medium text-[#0b3e65]">Reportes</th>
                <th className="text-left px-4 py-3 font-medium text-[#0b3e65]">Último login</th>
                <th className="text-left px-4 py-3 font-medium text-[#0b3e65]">Registrado</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3 text-slate-600">{u.company || "-"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      u.role === "admin" ? "bg-[#f37022]/10 text-[#f37022]" : "bg-slate-100 text-slate-600"
                    }`}>
                      {u.role === "admin" && <Shield className="w-3 h-3" />}
                      {u.role === "admin" ? "Admin" : "Usuario"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">{u.report_count || 0}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{u.last_login ? formatDate(u.last_login) : "-"}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============== INVITATIONS TAB ==============
const InvitationsTab = () => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", company: "", role: "user" });

  const loadInvitations = useCallback(() => {
    axios.get(`${API}/admin/invitations`)
      .then((res) => setInvitations(res.data))
      .catch(() => toast.error("Error cargando invitaciones"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadInvitations(); }, [loadInvitations]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.email || !form.company) {
      toast.error("Email y empresa son obligatorios");
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/admin/invitations`, form);
      const token = res.data.token;
      const link = `${window.location.origin}/register?token=${token}`;
      await navigator.clipboard.writeText(link);
      toast.success("Invitación creada. Enlace copiado al portapapeles.");
      setForm({ email: "", company: "", role: "user" });
      loadInvitations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error creando invitación");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/invitations/${id}`);
      toast.success("Invitación eliminada");
      loadInvitations();
    } catch {
      toast.error("Error eliminando invitación");
    }
  };

  const copyLink = (token) => {
    const link = `${window.location.origin}/register?token=${token}`;
    navigator.clipboard.writeText(link);
    toast.success("Enlace copiado al portapapeles");
  };

  return (
    <div className="space-y-6">
      {/* Create invitation form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base text-[#0b3e65] flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Nueva Invitación
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreate} className="flex flex-wrap gap-3 items-end" data-testid="invitation-form">
            <div className="flex-1 min-w-[200px]">
              <Label htmlFor="inv-email">Email</Label>
              <Input
                id="inv-email"
                type="email"
                placeholder="usuario@empresa.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="rounded-sm mt-1"
                data-testid="invitation-email"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label htmlFor="inv-company">Empresa</Label>
              <Input
                id="inv-company"
                type="text"
                placeholder="Nombre de la empresa"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                required
                className="rounded-sm mt-1"
                data-testid="invitation-company"
              />
            </div>
            <div className="w-[140px]">
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="invitation-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Usuario</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              type="submit"
              disabled={creating}
              className="bg-[#0b3e65] hover:bg-[#0b3e65]/90 rounded-sm"
              data-testid="create-invitation-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />}
              Crear y copiar enlace
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Invitations list */}
      {loading ? <LoadingSpinner /> : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
            <h3 className="font-medium text-[#0b3e65]">Invitaciones ({invitations.length})</h3>
          </div>
          {invitations.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No hay invitaciones todavía</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="invitations-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left px-4 py-2 font-medium text-slate-600">Email</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">Empresa</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">Rol</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">Estado</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">Creada</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((inv) => (
                    <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-2">{inv.email}</td>
                      <td className="px-4 py-2 text-slate-600">{inv.company}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          inv.role === "admin" ? "bg-[#f37022]/10 text-[#f37022]" : "bg-slate-100 text-slate-600"
                        }`}>
                          {inv.role === "admin" ? "Admin" : "Usuario"}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        {inv.used ? (
                          <span className="text-green-600 text-xs font-medium">Utilizada</span>
                        ) : (
                          <span className="text-amber-600 text-xs font-medium">Pendiente</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500">{formatDate(inv.created_at)}</td>
                      <td className="px-4 py-2">
                        <div className="flex gap-1">
                          {!inv.used && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyLink(inv.token)}
                                className="h-7 px-2"
                                data-testid={`copy-invite-${inv.email}`}
                              >
                                <ClipboardCopy className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(inv.id)}
                                className="h-7 px-2 text-red-500 hover:text-red-700"
                                data-testid={`delete-invite-${inv.email}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============== ACTIVITY TAB ==============
const ActivityTab = () => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [companyFilter, setCompanyFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const loadActivity = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 20 });
    if (companyFilter) params.set("company", companyFilter);
    if (dateFrom) params.set("date_from", new Date(dateFrom).toISOString());
    if (dateTo) params.set("date_to", new Date(dateTo + "T23:59:59").toISOString());

    axios.get(`${API}/admin/activity?${params}`)
      .then((res) => {
        setEntries(res.data.entries);
        setTotal(res.data.total);
        setPages(res.data.pages);
      })
      .catch(() => toast.error("Error cargando actividad"))
      .finally(() => setLoading(false));
  }, [page, companyFilter, dateFrom, dateTo]);

  useEffect(() => { loadActivity(); }, [loadActivity]);

  const actionLabel = (a) => {
    const map = { screening: "Screening", export_excel: "Export Excel", register: "Registro", create_invitation: "Invitación creada" };
    return map[a] || a;
  };

  const resultBadge = (result) => {
    if (!result) return null;
    const colors = {
      "GO": "bg-green-100 text-green-700",
      "CAUTION": "bg-amber-100 text-amber-700",
      "NO GO": "bg-red-100 text-red-700",
      "INCOMPLETO": "bg-slate-100 text-slate-600",
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[result] || "bg-slate-100 text-slate-600"}`}>
        {result}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-3 items-end" data-testid="activity-filters">
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs">Empresa</Label>
              <div className="relative mt-1">
                <Building2 className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Filtrar por empresa"
                  value={companyFilter}
                  onChange={(e) => { setCompanyFilter(e.target.value); setPage(1); }}
                  className="pl-9 rounded-sm"
                  data-testid="activity-filter-company"
                />
              </div>
            </div>
            <div className="w-[170px]">
              <Label className="text-xs">Desde</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="rounded-sm mt-1"
                data-testid="activity-filter-from"
              />
            </div>
            <div className="w-[170px]">
              <Label className="text-xs">Hasta</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="rounded-sm mt-1"
                data-testid="activity-filter-to"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => { setCompanyFilter(""); setDateFrom(""); setDateTo(""); setPage(1); }}
              className="rounded-sm"
              data-testid="activity-clear-filters"
            >
              Limpiar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Activity table */}
      {loading ? <LoadingSpinner /> : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
            <h3 className="font-medium text-[#0b3e65]">Log de actividad ({total} entradas)</h3>
          </div>
          {entries.length === 0 ? (
            <div className="p-8 text-center text-slate-500">Sin actividad registrada</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="activity-table">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left px-4 py-2 font-medium text-slate-600">Fecha/Hora</th>
                      <th className="text-left px-4 py-2 font-medium text-slate-600">Usuario</th>
                      <th className="text-left px-4 py-2 font-medium text-slate-600">Email</th>
                      <th className="text-left px-4 py-2 font-medium text-slate-600">Empresa</th>
                      <th className="text-left px-4 py-2 font-medium text-slate-600">Acción</th>
                      <th className="text-left px-4 py-2 font-medium text-slate-600">IMO</th>
                      <th className="text-left px-4 py-2 font-medium text-slate-600">Buque</th>
                      <th className="text-left px-4 py-2 font-medium text-slate-600">Resultado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e) => (
                      <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`activity-row-${e.id}`}>
                        <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">{formatDateTime(e.timestamp)}</td>
                        <td className="px-4 py-2 font-medium">{e.user_name}</td>
                        <td className="px-4 py-2 text-slate-600">{e.user_email}</td>
                        <td className="px-4 py-2 text-slate-600">{e.company || "-"}</td>
                        <td className="px-4 py-2">
                          <span className="flex items-center gap-1">
                            {e.action === "screening" && <Ship className="w-3.5 h-3.5 text-[#0b3e65]" />}
                            {e.action === "export_excel" && <Download className="w-3.5 h-3.5 text-green-600" />}
                            {e.action === "register" && <UserPlus className="w-3.5 h-3.5 text-blue-600" />}
                            {e.action === "create_invitation" && <Mail className="w-3.5 h-3.5 text-amber-600" />}
                            {actionLabel(e.action)}
                          </span>
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">{e.imo_number || "-"}</td>
                        <td className="px-4 py-2">{e.vessel_name || "-"}</td>
                        <td className="px-4 py-2">{resultBadge(e.result)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {pages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200" data-testid="activity-pagination">
                  <span className="text-sm text-slate-500">Página {page} de {pages}</span>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ============== STATS TAB ==============
const StatsTab = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/stats`)
      .then((res) => setStats(res.data))
      .catch(() => toast.error("Error cargando estadísticas"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;
  if (!stats) return null;

  const statCards = [
    { label: "Usuarios", value: stats.total_users, icon: Users, color: "bg-[#0b3e65]" },
    { label: "Reportes totales", value: stats.total_reports, icon: FileBarChart, color: "bg-[#f37022]" },
    { label: "Reportes esta semana", value: stats.reports_this_week, icon: Clock, color: "bg-emerald-600" },
    { label: "Invitaciones pendientes", value: stats.pending_invitations, icon: Mail, color: "bg-amber-600" },
  ];

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-cards">
        {statCards.map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.label}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-4">
                  <div className={`${s.color} text-white p-3 rounded-lg`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#0b3e65]">{s.value}</p>
                    <p className="text-xs text-slate-500">{s.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Risk breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base text-[#0b3e65]">Resultados por tipo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="risk-breakdown">
            {[
              { label: "GO", count: stats.risk_counts?.green || 0, color: "bg-green-500" },
              { label: "CAUTION", count: stats.risk_counts?.yellow || 0, color: "bg-amber-500" },
              { label: "NO GO", count: stats.risk_counts?.red || 0, color: "bg-red-500" },
              { label: "INCOMPLETO", count: stats.risk_counts?.incomplete || 0, color: "bg-slate-400" },
            ].map((r) => (
              <div key={r.label} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                <div className={`w-3 h-3 rounded-full ${r.color}`} />
                <div>
                  <p className="text-lg font-bold">{r.count}</p>
                  <p className="text-xs text-slate-500">{r.label}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top users */}
      {stats.top_users?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base text-[#0b3e65]">Usuarios más activos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="top-users">
              {stats.top_users.map((u, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-slate-400 w-6">{i + 1}.</span>
                    <span className="text-sm font-medium">{u._id || u.company}</span>
                  </div>
                  <span className="text-sm font-bold text-[#0b3e65]">{u.count} reportes</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============== HELPERS ==============
const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-12">
    <Loader2 className="w-8 h-8 animate-spin text-[#0b3e65]" />
  </div>
);

const formatDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

const formatDateTime = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default AdminDashboardPage;
